import {
  Banknote,
  CircleCheck,
  Clock3,
  FileText,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { formatCurrency } from "@/domain/billing";
import type {
  CurrencyCode,
  DashboardSummary,
} from "@/domain/types";

function currencySummary(
  values: Partial<Record<CurrencyCode, number>>,
): string {
  const currencies = Object.keys(
    values,
  ) as CurrencyCode[];

  if (currencies.length === 0) {
    return "$0.00";
  }

  return currencies
    .map((currency) =>
      formatCurrency(values[currency] ?? 0, currency),
    )
    .join(" · ");
}

export function DashboardStats({
  summary,
}: {
  summary: DashboardSummary;
}) {
  const outstanding = currencySummary(
    summary.outstandingCentsByCurrency,
  );

  const paid = currencySummary(
    summary.paidCentsByCurrency,
  );

  const cards = [
    {
      label: "All invoices",
      value: String(summary.totalInvoices),
      icon: FileText,
      note: `${summary.draftCount} draft`,
    },
    {
      label: "Awaiting payment",
      value: String(
        summary.sentCount + summary.overdueCount,
      ),
      icon: Clock3,
      note: `${summary.overdueCount} overdue`,
    },
    {
      label: "Outstanding",
      value: outstanding,
      icon: Banknote,
      note: "Grouped by currency",
    },
    {
      label: "Paid invoices",
      value: String(summary.paidCount),
      icon: CircleCheck,
      note:
        summary.paidCount > 0
          ? `Received: ${paid}`
          : "Receipts available after payment",
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map(
        ({ label, value, icon: Icon, note }) => (
          <Card key={label} className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-ink-muted">
                  {label}
                </p>
                <p className="mt-2 break-words text-2xl font-black text-ink">
                  {value}
                </p>
                <p className="mt-1 break-words text-xs text-ink-muted">
                  {note}
                </p>
              </div>

              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                <Icon className="h-5 w-5" />
              </div>
            </div>
          </Card>
        ),
      )}
    </div>
  );
}
